import React, { useRef, useCallback } from "react";
import { TextField, InputAdornment, IconButton, Box, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { InsertEmoticon, Mic, Send, AutoAwesome } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ChannelSelector, { INTERNAL_NOTE_CHANNEL_ID } from "./ChannelSelector";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import {
  COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
  COMPOSER_MOBILE_ROW_HEIGHT_PX,
  COMPOSER_ROW_HEIGHT_PX,
  composerMobileInputRowSx,
  composerPrimaryActionButtonSx,
} from "./composerConstants";
import { COMPOSER_MOBILE_TEXT_FIELD_SX, COMPOSER_TEXT_FIELD_SX } from "./composerTextFieldStyles";
import ComposerMobileToolbar from "./ComposerMobileToolbar";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";

const MessageAppChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  startRecording,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  onSendCurrentLocation,
  locationSending = false,
  locationSendDisabled = false,
  locationSendDisabledHelp = null,
  sendBlockedMissingPhone = false,
  onOpenProductPicker,
  isRecording = false,
  recordingDuration = 0,
  stopRecording = () => {},
  discardRecording = () => {},
  onRemoveAudio,
  clientConversation = null,
  conversationMessages = null,
}) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const inputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputBorderRadius = 16;
  const composerRowHeight = isMobile ? COMPOSER_MOBILE_ROW_HEIGHT_PX : COMPOSER_ROW_HEIGHT_PX;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const isInternalNote =
    String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID) || channelType === "Xpro";
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote,
  });
  const showAiButton = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const canSend = composerHasSendPayload(message, file, files, audioURL);

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const handleOpenProductPickerFromMenu = useCallback(() => {
    onOpenProductPicker?.();
    queueMicrotask(() => inputRef.current?.focus());
  }, [onOpenProductPicker]);

  const textField = (
    <TextField
      fullWidth
      multiline
      minRows={1}
      maxRows={4}
      variant="outlined"
      placeholder={
        channelType === "Xpro"
          ? t("input.placeholderInternal")
          : t("input.placeholderChannel", { channel: channelType })
      }
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      inputProps={composeInputHandlers}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSendMessage();
        }
      }}
      InputProps={{
        style: { borderRadius: 0, fontSize: isMobile ? "16px" : "1rem" },
        inputRef: inputRef,
        startAdornment: isMobile ? undefined : (
          <InputAdornment position="start" sx={{ ml: 0.5 }}>
            <IconButton
              onClick={handleEmojiPickerOpen}
              edge="start"
              size="medium"
              aria-label={t("input.emojiAria")}
            >
              <InsertEmoticon fontSize="medium" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: isMobile ? undefined : (
          <InputAdornment
            position="end"
            sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}
          >
            {showAiButton ? (
              <Tooltip
                title={
                  aiDisabled
                    ? isGeneratingSuggestion
                      ? t("input.aiGenerating")
                      : t("input.aiNoHistory")
                    : t("input.aiSuggest")
                }
              >
                <span>
                  <IconButton
                    onClick={onGenerateSuggestion}
                    disabled={aiDisabled}
                    size="medium"
                    aria-label={t("input.aiSuggest")}
                  >
                    <AutoAwesome fontSize="medium" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <ComposeAttachMenu
              openFilePicker={openFilePicker}
              showAttachProducts={typeof onOpenProductPicker === "function"}
              onOpenProductPicker={handleOpenProductPickerFromMenu}
              showLocation={typeof onSendCurrentLocation === "function"}
              onSendCurrentLocation={onSendCurrentLocation}
              locationSendDisabled={locationSendDisabled}
              locationSending={locationSending}
              locationSendDisabledHelp={locationSendDisabledHelp}
              size="medium"
            />
          </InputAdornment>
        ),
      }}
      sx={isMobile ? COMPOSER_MOBILE_TEXT_FIELD_SX : COMPOSER_TEXT_FIELD_SX}
    />
  );

  const textOrVoiceSlot = isRecording ? (
    <RecordingIndicator
      embedded
      durationSeconds={recordingDuration}
      onStop={stopRecording}
      onDiscard={discardRecording}
    />
  ) : audioURL ? (
    <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
  ) : (
    textField
  );

  const integratedComposer = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "stretch",
        borderRadius: `${inputBorderRadius}px`,
        minHeight: composerRowHeight,
        overflow: "hidden",
        ...composerSurface.shell,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          alignSelf: "stretch",
          px: 0.25,
          ...composerSurface.divider,
        }}
      >
        <ChannelSelector
          integrated
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          clientConversation={clientConversation}
          conversationMessages={conversationMessages}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: `${composerRowHeight}px`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {textOrVoiceSlot}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        paddingBottom: 0,
      }}
    >
      <Box
        sx={
          isMobile
            ? composerMobileInputRowSx
            : {
                display: "flex",
                alignItems: "center",
                gap: 0,
                width: "100%",
              }
        }
      >
        {integratedComposer}
        {isMobile && !isRecording && !audioURL ? (
        <ComposerMobileToolbar>
          <IconButton
            onClick={handleEmojiPickerOpen}
            size="small"
            aria-label={t("input.emojiAria")}
          >
            <InsertEmoticon fontSize="small" />
          </IconButton>
          {showAiButton ? (
            <Tooltip
              title={
                aiDisabled
                  ? isGeneratingSuggestion
                    ? t("input.aiGenerating")
                    : t("input.aiNoHistory")
                  : t("input.aiSuggest")
              }
            >
              <span>
                <IconButton
                  onClick={onGenerateSuggestion}
                  disabled={aiDisabled}
                  size="small"
                  aria-label={t("input.aiSuggest")}
                >
                  <AutoAwesome fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <ComposeAttachMenu
            openFilePicker={openFilePicker}
            showAttachProducts={typeof onOpenProductPicker === "function"}
            onOpenProductPicker={handleOpenProductPickerFromMenu}
            showLocation={typeof onSendCurrentLocation === "function"}
            onSendCurrentLocation={onSendCurrentLocation}
            locationSendDisabled={locationSendDisabled}
            locationSending={locationSending}
            locationSendDisabledHelp={locationSendDisabledHelp}
            size="small"
          />
        </ComposerMobileToolbar>
        ) : null}
        {isMobile ? (
          <IconButton
            onClick={() => {
              if (sendBlockedMissingPhone) return;
              if (canSend) handleSendMessage();
              else startRecording();
            }}
            disabled={sendBlockedMissingPhone}
            aria-label={
              canSend ? t("input.sendAria") : t("input.recordVoiceAria")
            }
            sx={composerPrimaryActionButtonSx({ size: COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX })}
          >
            {canSend ? (
              <Send fontSize="small" />
            ) : (
              <Mic fontSize="small" />
            )}
          </IconButton>
        ) : null}
      </Box>
      {selectionToolbar}
    </Box>
  );
};

export default MessageAppChannel;
